import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, Typography } from "@mui/material";
import { getDevtoolsAuthToken } from "../../devtools/utils/devtoolsAuth";

// A managed asset is always loaded with the same authenticated Document Manager
// endpoint; never expose unvalidated Markdown paths directly as image src.
const VALID_REF = /^assets\/([0-9a-f]{64}\.(?:png|jpg|webp))$/;

const ManagedDocumentImage = ({ projectId, slug, reference, alt = "Document image" }) => {
  const [imageUrl, setImageUrl] = useState("");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const match = typeof reference === "string" ? reference.match(VALID_REF) : null;
  const assetName = match?.[1] || "";
  useEffect(() => {
    let active = true;
    let objectUrl = "";
    setImageUrl("");
    setError("");
    if (!slug || !projectId || !assetName) {
      setError("This image is not a managed document asset.");
      return undefined;
    }
    const token = getDevtoolsAuthToken();
    fetch(`/api/docs/documents/${encodeURIComponent(slug)}/assets/${encodeURIComponent(assetName)}?project_id=${encodeURIComponent(projectId)}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(async (response) => {
        if (!response.ok) throw new Error("Image not available in Document Manager.");
        const blob = await response.blob();
        if (!blob.type.startsWith("image/")) throw new Error("Invalid image response.");
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      })
      .catch((err) => { if (active) setError(err.message || "Unable to load image."); });
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [projectId, slug, assetName]);
  if (!assetName) return <Typography variant="caption" color="text.secondary">[Image: {alt} — unsafe or external image reference]</Typography>;
  return <Box sx={{ my: 2, minWidth: 0, maxWidth: "100%" }}>
    {error && <Typography variant="caption" color="error">{alt}: {error}</Typography>}
    {!imageUrl && !error && <CircularProgress size={20} />}
    {imageUrl && <>
      <Button title={`Expand ${alt}`} onClick={() => setOpen(true)} sx={{ p: 0, maxWidth: "100%", display: "block", textTransform: "none" }}>
        <Box component="img" src={imageUrl} alt={alt} sx={{ maxWidth: "100%", width: "auto", height: "auto", maxHeight: 650, objectFit: "contain", border: 1, borderColor: "divider", borderRadius: 1 }} />
      </Button>
      <Typography variant="caption" color="text.secondary">{alt} — click to enlarge</Typography>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>{alt} <Box sx={{ float: "right", display: "flex", gap: 1 }}>
          <Button onClick={() => setZoom((z) => Math.max(0.5, Number((z - 0.25).toFixed(2))))}>−</Button>
          <Typography variant="body2" sx={{ alignSelf: "center" }}>{Math.round(zoom * 100)}%</Typography>
          <Button onClick={() => setZoom((z) => Math.min(3, Number((z + 0.25).toFixed(2))))}>+</Button>
          <Button onClick={() => { setOpen(false); setZoom(1); }}>Close</Button>
        </Box></DialogTitle>
        <DialogContent dividers sx={{ textAlign: "center", overflow: "auto" }}>
          <Box component="img" src={imageUrl} alt={alt} sx={{ width: `${zoom * 100}%`, maxWidth: zoom <= 1 ? "100%" : "none", height: "auto", objectFit: "contain" }} />
        </DialogContent>
      </Dialog>
    </>}
  </Box>;
};
export default ManagedDocumentImage;
